import Standbild from "../bausteine/Standbild";
import { Abschnitt } from "../bausteine/Grundelemente";
import { useDemo } from "../zustand/DemoZustand";
import { meinungsbildFuerTop } from "../hilfen/auswertung";
import { langesDatum, uhrzeit } from "../hilfen/datum";

/*
  Die fünf Bereiche, die im Prototyp bewusst nicht bedienbar sind.
  Sie zeigen den vollen Funktionsumfang, ohne ihn vorzutäuschen.
*/

export const STANDBILDER = [
  {
    schluessel: "sitzungsleitung",
    titel: "Sitzungsleitung für den Fraktionsvorstand",
    kurz: "Automatisch erzeugter Leitfaden für die Fraktionssitzung am Dienstag.",
  },
  /*
    „Aufgabenübersicht“ stand hier als Standbild. Seit dem Aufgabenbrett gibt
    es sie wirklich — beides nebeneinander zu zeigen, hieße im Vorführtermin
    dasselbe zweimal anzubieten, einmal echt und einmal als Attrappe.
  */
  {
    schluessel: "dokumentendatenbank",
    titel: "Interne Dokumentendatenbank",
    kurz: "Eigene Anträge, Stellungnahmen und Entwürfe der Fraktion, gebündelt in Akten.",
  },
  {
    schluessel: "lebenszyklus",
    titel: "Lebenszyklus einer Vorlage",
    kurz: "Von der Veröffentlichung durch die Verwaltung bis zur Umsetzung des Beschlusses.",
  },
  {
    schluessel: "verwaltung",
    titel: "Verwaltungsansicht",
    kurz: "Ausblick: dieselbe Plattform aus Sicht der Kommunalverwaltung.",
  },
  {
    schluessel: "buergeransicht",
    titel: "Bürgeransicht",
    kurz: "Ausblick: was Bürgerinnen und Bürger öffentlich einsehen können.",
  },
];

export default function StandbildAnsicht({ welches }) {
  switch (welches) {
    case "sitzungsleitung":
      return <Sitzungsleitung />;
    case "dokumentendatenbank":
      return <Dokumentendatenbank />;
    case "lebenszyklus":
      return <Lebenszyklus />;
    case "verwaltung":
      return <Verwaltungsansicht />;
    case "buergeransicht":
      return <Buergeransicht />;
    default:
      return (
        <div className="karte" style={{ marginTop: "1.5rem" }}>
          <p>Dieser Bereich wurde nicht gefunden.</p>
        </div>
      );
  }
}

/* ---------- 1. Sitzungsleitung ---------- */

function Sitzungsleitung() {
  const { stammdaten, zustand } = useDemo();
  const fraktionssitzung = stammdaten.sitzungen["s-fraktion"];
  const inhaltliche = stammdaten.tagesordnung.filter((t) => t.vorlageId);

  /*
    Wer den Vortrag hält, ist im Standbild frei erfunden — die Software teilt
    das nicht zu. Genommen werden reihum die wirklichen Mitglieder der
    Fraktion, damit das Standbild zeigt, wie es aussähe. Erfundene Namen
    stehen hier bewusst nicht mehr: Sie wären von echten nicht zu
    unterscheiden.
  */
  const mitglieder = Object.values(stammdaten.personen);
  const sprecherFuer = (i) =>
    mitglieder.length ? mitglieder[i % mitglieder.length].name : "noch offen";
  const minuten = { "top-3": 25, "top-4": 10, "top-5": 10, "top-6": 15, "top-7": 20 };

  return (
    <Standbild
      vorspann="Fraktionsvorstand"
      titel="Sitzungsleitung"
      erklaerung={`Erzeugt aus dem Stand des Meinungsbilds, den eingereichten Fragen und den offenen Aufgaben. Für ${langesDatum(fraktionssitzung.beginn)}, ${uhrzeit(fraktionssitzung.beginn)}, ${fraktionssitzung.ort}.`}
      spaeter="Der Vorstand erzeugt den Leitfaden mit einem Klick, kann Redezeiten und Sprecher ändern und ihn als PDF an alle verteilen. Nach der Sitzung entstehen daraus automatisch die Aufgaben."
    >
      <div className="karte">
        <p style={{ fontWeight: 700 }}>Gesamtdauer geplant: 80 Minuten</p>
        <p className="klein leise" style={{ marginTop: "var(--luft-2)" }}>
          9 zugesagt · 1 abgesagt · 2 unklar · 1 ohne Rückmeldung
        </p>
      </div>

      {inhaltliche.map((top) => {
        const mb = meinungsbildFuerTop(zustand, stammdaten, top.id);
        const gespalten = mb.dafuer.length > 0 && mb.dagegen.length > 0;
        return (
          <div key={top.id} className="karte" style={{ marginTop: "var(--luft-3)" }}>
            <p className="klein leise">
              TOP {top.nummer} · {minuten[top.id]} Minuten · Vortrag:{" "}
              {sprecherFuer(inhaltliche.indexOf(top))}
            </p>
            <p style={{ fontWeight: 700, marginTop: "var(--luft-2)" }}>{top.titel}</p>

            <p className="klein" style={{ marginTop: "var(--luft-3)" }}>
              Stand: {mb.dafuer.length} dafür · {mb.dagegen.length} dagegen ·{" "}
              {mb.unentschieden.length} unentschieden · {mb.offen.length} ohne Rückmeldung
            </p>

            <p
              className="klein"
              style={{ marginTop: "var(--luft-2)", color: "var(--akzent-dunkel)" }}
            >
              <strong>Hinweis für die Leitung:</strong>{" "}
              {gespalten
                ? "Die Fraktion ist geteilt. Beide Seiten kurz begründen lassen, dann Änderungsantrag prüfen."
                : mb.offen.length > 6
                  ? "Kaum Rückmeldungen. Erst gemeinsam durchgehen, dann Meinungsbild wiederholen."
                  : "Weitgehend einig. Nur offene Fragen abarbeiten, keine Grundsatzdebatte."}
            </p>
          </div>
        );
      })}
    </Standbild>
  );
}

/* ---------- 2. Dokumentendatenbank ---------- */

function Dokumentendatenbank() {
  const akten = [
    {
      name: "Akte: Bebauungsplan Nr. 47 „Am Mühlenweg“",
      eintraege: [
        ["VL/2026/184 — Auslegungsbeschluss", "Verwaltung", "42 Seiten", "vor 9 Tagen"],
        ["Entwurf Änderungsantrag Stellplatzschlüssel", "R. Buschmann", "Fassung 3", "vor 2 Tagen"],
        ["Notizen Ortstermin Anwohnerinitiative", "K. Meinhardt", "Fassung 1", "vor 6 Tagen"],
        ["Verkehrsgutachten Prenzel + Partner", "Verwaltung", "28 Seiten", "vor 9 Tagen"],
      ],
    },
    {
      name: "Akte: Tempo 30 vor Kitas",
      eintraege: [
        ["A/2026/033 — Antrag der Fraktion", "Fraktion", "2 Seiten", "vor 21 Tagen"],
        ["VL/2026/188 — Stellungnahme der Verwaltung", "Verwaltung", "3 Seiten", "vor 4 Tagen"],
        ["Schreiben der Kita Sonnenblume", "Extern", "1 Seite", "vor 30 Tagen"],
      ],
    },
    {
      name: "Akte: Haushalt 2027",
      eintraege: [
        ["Sammlung Haushaltsanträge", "B. Klingemann", "Fassung 7", "vor 1 Tag"],
        ["Eckwertebeschluss Kämmerei", "Verwaltung", "16 Seiten", "vor 12 Tagen"],
      ],
    },
  ];

  return (
    <Standbild
      vorspann="Fraktion Bürgerforum"
      titel="Interne Dokumentendatenbank"
      erklaerung="Eigene Dokumente der Fraktion und Vorlagen der Verwaltung liegen zum selben Thema in einer gemeinsamen Akte."
      spaeter="Dokumente werden gemeinsam bearbeitet, jede Fassung bleibt nachvollziehbar. Aus jedem Dokument heraus lassen sich Aufgaben anlegen und Chatbeiträge verknüpfen."
    >
      {akten.map((akte) => (
        <Abschnitt key={akte.name} titel={akte.name}>
          <div className="tabelle-huelle">
            <table className="tabelle">
              <thead>
                <tr>
                  <th>Dokument</th>
                  <th>Herkunft</th>
                  <th>Umfang</th>
                  <th>Zuletzt geändert</th>
                </tr>
              </thead>
              <tbody>
                {akte.eintraege.map((zeile) => (
                  <tr key={zeile[0]}>
                    {zeile.map((zelle, i) => (
                      <td key={i}>{zelle}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Abschnitt>
      ))}
    </Standbild>
  );
}

/* ---------- 3. Lebenszyklus einer Vorlage ---------- */

function Lebenszyklus() {
  const { stammdaten } = useDemo();
  const vorlage = stammdaten.vorlagen["vl-184"];

  const schritte = [
    {
      titel: "Vorlage von der Verwaltung veröffentlicht",
      text: "Fachbereich 6 stellt die Vorlage ins Ratsinformationssystem ein. Fraktionsbüro übernimmt sie automatisch.",
      stand: "erledigt",
      wann: "vor 9 Tagen",
    },
    {
      titel: "Ladung und Tagesordnung",
      text: "Die Ladung zur Ausschusssitzung geht förmlich über die Verwaltung. Fraktionsbüro zeigt sie nur an.",
      stand: "erledigt",
      wann: "vor 8 Tagen",
    },
    {
      titel: "Lesen, markieren, diskutieren",
      text: "Die Fraktion arbeitet die Vorlage durch. 11 Markierungen, 5 Beiträge im Fraktionschat, 1 Rückfrage ans Bauamt.",
      stand: "erledigt",
      wann: "seit 6 Tagen",
    },
    {
      titel: "Meinungsbild und Fraktionssitzung",
      text: "Positionen werden vorab erfasst, die Fraktionssitzung klärt die strittigen Punkte und legt die Linie fest.",
      stand: "jetzt",
      wann: "Dienstag",
    },
    {
      titel: "Beratung und Beschluss im Ausschuss",
      text: "Findet im Gremium statt. Die Beschlussfassung ist ein amtlicher Akt und bleibt beim Ratsinformationssystem.",
      stand: "offen",
      wann: "Donnerstag",
    },
    {
      titel: "Niederschrift",
      text: "Die Verwaltung erstellt die Niederschrift. Fraktionsbüro liest sie ein und hängt sie an die Akte.",
      stand: "offen",
      wann: "in etwa 2 Wochen",
    },
    {
      titel: "Umsetzung und Nachverfolgung",
      text: "Offene Punkte werden zu Aufgaben. Die Fraktion sieht, was aus ihrem Beschluss geworden ist.",
      stand: "offen",
      wann: "laufend",
    },
  ];

  return (
    <Standbild
      vorspann={vorlage.aktenzeichen}
      titel="Lebenszyklus einer Vorlage"
      erklaerung="Wo dieser Vorgang gerade steht — und welche Schritte bewusst außerhalb von Fraktionsbüro stattfinden."
      spaeter="Jeder Schritt ist anklickbar und führt zu den Dokumenten, Beiträgen und Aufgaben, die dabei entstanden sind."
    >
      <ol className="strahl">
        {schritte.map((s) => (
          <li
            key={s.titel}
            className={`strahl__punkt ${
              s.stand === "erledigt"
                ? "strahl__punkt--erledigt"
                : s.stand === "jetzt"
                  ? "strahl__punkt--jetzt"
                  : ""
            }`}
          >
            <p className="klein leise">{s.wann}</p>
            <p className="strahl__titel">{s.titel}</p>
            <p className="klein" style={{ marginTop: "var(--luft-1)" }}>
              {s.text}
            </p>
          </li>
        ))}
      </ol>
    </Standbild>
  );
}

/* ---------- 4. Verwaltungsansicht ---------- */

function Verwaltungsansicht() {
  const vorgaenge = [
    ["A/2026/033", "Tempo 30 vor Kitas", "Fraktion Bürgerforum", "Stellungnahme abgegeben", "FB 5"],
    ["AF/2026/091", "Anfrage Lärmschutz B14", "Fraktion Bürgerforum", "In Bearbeitung, Frist in 9 Tagen", "FB 5"],
    ["AF/2026/088", "Anfrage Sanierungsstau Turnhallen", "Fraktion A", "Beantwortet", "FB 4"],
    ["A/2026/031", "Antrag Schulwegsicherung", "Fraktion B", "Zur Beratung terminiert", "FB 5"],
    ["AF/2026/094", "Anfrage Stellplatzschlüssel B-Plan 47", "Fraktion Bürgerforum", "Neu eingegangen", "FB 6"],
  ];

  return (
    <Standbild
      vorspann="Ausblick"
      titel="Verwaltungsansicht"
      erklaerung="Dieselbe Plattform aus Sicht der Kommunalverwaltung. Anträge und Anfragen der Fraktionen kommen strukturiert an, statt als E-Mail-Anhang."
      spaeter="Die Verwaltung sieht Fristen und Zuständigkeiten auf einen Blick, antwortet direkt in der Plattform, und beide Seiten sehen denselben Stand. Der amtliche Weg über das Ratsinformationssystem bleibt davon unberührt."
    >
      <div className="tabelle-huelle">
        <table className="tabelle">
          <thead>
            <tr>
              <th>Kennzeichen</th>
              <th>Betreff</th>
              <th>Von</th>
              <th>Stand</th>
              <th>Fachbereich</th>
            </tr>
          </thead>
          <tbody>
            {vorgaenge.map((z) => (
              <tr key={z[0]}>
                {z.map((zelle, i) => (
                  <td key={i}>{zelle}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="klein leise" style={{ marginTop: "var(--luft-4)" }}>
        Die Fraktionsnamen sind hier bewusst neutral gehalten. In einer echten Kommune stünden an
        dieser Stelle die tatsächlichen Fraktionen des Rates.
      </p>
    </Standbild>
  );
}

/* ---------- 5. Bürgeransicht ---------- */

function Buergeransicht() {
  const { stammdaten } = useDemo();
  const ausschuss = stammdaten.sitzungen[stammdaten.ausschussId];

  const beschluesse = [
    ["Radwegverbindung Feldstraße, Bauabschnitt 2", "einstimmig beschlossen", "vor 4 Wochen"],
    ["Erhöhung der Zuschüsse für Sportvereine", "mehrheitlich beschlossen", "vor 4 Wochen"],
    ["Aufstellungsbeschluss Bebauungsplan Nr. 48", "vertagt", "vor 8 Wochen"],
  ];

  return (
    <Standbild
      vorspann="Ausblick"
      titel="Bürgeransicht"
      erklaerung={`Öffentlich einsehbar: Sitzungstermine, Tagesordnungen und gefasste Beschlüsse der ${stammdaten.koerperschaft.name}. Nichtöffentliche Punkte erscheinen hier nicht.`}
      spaeter="Bürgerinnen und Bürger finden Termine und Beschlüsse ohne Vorkenntnisse über Aktenzeichen. Wer möchte, abonniert einzelne Themen und wird bei neuen Vorlagen benachrichtigt."
    >
      <Abschnitt titel="Nächste öffentliche Sitzung">
        <div className="karte">
          <p style={{ fontWeight: 700 }}>{stammdaten.gremien[ausschuss.gremiumId].name}</p>
          <p className="klein leise" style={{ marginTop: "var(--luft-2)" }}>
            {langesDatum(ausschuss.beginn)}, {uhrzeit(ausschuss.beginn)} · {ausschuss.ort}
          </p>
          <p className="klein" style={{ marginTop: "var(--luft-3)" }}>
            8 öffentliche Tagesordnungspunkte · 1 Punkt wird nichtöffentlich beraten
          </p>
        </div>
      </Abschnitt>

      <Abschnitt titel="Zuletzt gefasste Beschlüsse">
        <div className="tabelle-huelle">
          <table className="tabelle">
            <thead>
              <tr>
                <th>Gegenstand</th>
                <th>Ergebnis</th>
                <th>Wann</th>
              </tr>
            </thead>
            <tbody>
              {beschluesse.map((z) => (
                <tr key={z[0]}>
                  {z.map((zelle, i) => (
                    <td key={i}>{zelle}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Abschnitt>

      <p className="klein leise" style={{ marginTop: "var(--luft-4)" }}>
        Verbindlich bleibt allein die Bekanntmachung der Stadt. Diese Ansicht ist eine
        Lesehilfe, kein amtliches Verzeichnis.
      </p>
    </Standbild>
  );
}
